import React, { createContext, useContext, useEffect } from "react";
import { CREATOR_PAGES_FROM } from "../componentsTable";
import {
  isDashboardPathname,
  resolveDashboardCollectionFrom,
} from "../url/buildCollectionViewAllDestination";

export type OpenCollectionIn = "iframe" | "new_window";

const OpenCollectionInContext = createContext<OpenCollectionIn>("iframe");

let openCollectionInModuleValue: OpenCollectionIn = "iframe";

export function resolveOpenCollectionIn(value: unknown): OpenCollectionIn {
  return value === "new_window" ? "new_window" : "iframe";
}

export function getOpenCollectionIn(): OpenCollectionIn {
  return openCollectionInModuleValue;
}

function syncOpenCollectionInModule(value: OpenCollectionIn) {
  openCollectionInModuleValue = value;
}

export function isEmbeddableCollectionView(from: unknown): boolean {
  const normalized = String(from || "");
  return normalized === "embed-search" || normalized==="landing-page" || normalized === CREATOR_PAGES_FROM;
}

/** Embed / multipage iframes cannot rely on window scroll for infinite scroll — use an explicit Load More control. */
export function shouldUseEmbedLoadMoreButton(from: unknown): boolean {
  return isEmbeddableCollectionView(from);
}

/** True when the app is running inside a parent iframe on an embed / creator-pages surface. */
export function isEmbedIframeContext(from: unknown): boolean {
  return (
    typeof window !== "undefined" &&
    window.parent !== window &&
    isEmbeddableCollectionView(from)
  );
}

/** True when a carousel / View All click should open a new browser tab instead of in-embed navigation. */
export function shouldOpenCollectionInNewWindow(
  from: unknown,
  openCollectionIn: OpenCollectionIn,
  pathname?: string
): boolean {
  const normalizedFrom = String(from || "");
  const path =
    pathname ??
    (typeof window !== "undefined" ? window.location.pathname : "");

  if (isDashboardPathname(path)) {
    const dashboardFrom = resolveDashboardCollectionFrom(path) || normalizedFrom;
    if (
      dashboardFrom === "embed-search" ||
      dashboardFrom === CREATOR_PAGES_FROM ||
      dashboardFrom === "landing-page"
    ) {
      // In dashboard context, always open the live public page in a new tab.
      // This avoids cross-origin navigate() failures when the creator's preferred
      // domain (browsable.bio) differs from the dashboard origin (browsable.ai).
      return true;
    }
    return false;
  }

  if (!isEmbeddableCollectionView(from)) {
    return false;
  }

  if (
    normalizedFrom === CREATOR_PAGES_FROM &&
    typeof window !== "undefined" &&
    window.parent === window
  ) {
    return false;
  }

  return openCollectionIn === "new_window";
}

export function OpenCollectionInProvider({
  value,
  children,
}: {
  value: OpenCollectionIn;
  children: React.ReactNode;
}) {
  const resolved = resolveOpenCollectionIn(value);

  useEffect(() => {
    syncOpenCollectionInModule(resolved);
    return () => syncOpenCollectionInModule("iframe");
  }, [resolved]);

  return React.createElement(
    OpenCollectionInContext.Provider,
    { value: resolved },
    children
  );
}

export function useOpenCollectionIn(): OpenCollectionIn {
  return useContext(OpenCollectionInContext);
}
